// Device timeline maths for the Bulk Shell run page: the time axis, its tick
// labels and each device's bar. Pure, with no `@/` imports, so `npm test`
// runs it under node --test (relative imports carry `.ts`).
import type { ShellJob, ShellJobItem } from './shell-job.service';
import { formatDateTime } from './timezone.ts';

export interface Axis {
  start: number;
  end: number;
}

export interface Tick {
  pct: number;
  /** "2:01:23 PM"; minutes only when the axis spans more than 10 minutes. */
  time: string;
  /** Muted line above the time: the first tick (with the GMT offset), then only where the date changes. */
  date?: string;
}

export type BarKind = 'done' | 'running' | 'waiting' | 'none' | 'sent';

export interface Bar {
  kind: BarKind;
  /** Where the gray queued segment ends and the coloured one starts, in % of the axis. */
  sentPct: number;
  /** Where the coloured segment ends, in % of the axis. */
  endPct: number;
  /** Set when the device ran after the axis end: the bar is pinned right with this label ("+2 d"). */
  late?: string;
  /** No-timeout runs: an answer that came after the axis end ("+1 h 31 min"); the bar runs to the edge. */
  lateAnswer?: string;
}

const MIN_SPAN_MS = 10_000;
const SECONDS_UP_TO_MS = 10 * 60_000;
const PIN_PCT = 3;
const MIN_BAR_PCT = 0.6;
// One collator for every comparison: localeCompare with options builds a new one per call.
const byDeviceNumber = new Intl.Collator('en', { numeric: true });

const toMs = (iso?: string) => (iso ? Date.parse(iso) : NaN);
const clampPct = (v: number) => Math.min(100, Math.max(0, v));
const pctOf = (t: number, axis: Axis) => clampPct(((t - axis.start) / (axis.end - axis.start)) * 100);

/**
 * From the job's start to the last answer of its first pass, or to now while
 * that pass is still going. Devices that ran later because they came back
 * online (caught_up) do not stretch it — they are pinned to the right edge —
 * so a run kept open for days stays readable. `nowMs` is the browser clock.
 */
export function timelineAxis(
  job: Pick<ShellJob, 'started_at' | 'status'> & Partial<Pick<ShellJob, 'timeout_ms' | 'send_only' | 'slot_release_ms'>>,
  items: ShellJobItem[],
  nowMs: number,
): Axis {
  const started = toMs(job.started_at);
  const start = Number.isFinite(started) ? started : nowMs;
  if (job.timeout_ms === 0 && !job.send_only) {
    return noTimeoutAxis(start, job.slot_release_ms ?? 60_000, items, nowMs, job.status === 'running' || job.status === 'waiting');
  }
  const live = job.status === 'running' || job.status === 'waiting';
  let end = start;
  for (const it of items) {
    if (it.caught_up) continue;
    if (live && (it.status === 'pending' || it.status === 'running')) end = Math.max(end, nowMs);
    const last = toMs(it.finished_at ?? it.started_at);
    if (Number.isFinite(last)) end = Math.max(end, last);
  }
  return { start, end: Math.max(end, start + MIN_SPAN_MS) };
}

/**
 * A no-timeout run: its sends, and the answers that came within a slot release
 * of their send. Later answers are "late" (the bar runs off the edge); devices
 * sent after coming back online are pinned right, as caught-up devices are.
 * While batches are still going out the axis runs to now.
 */
function noTimeoutAxis(start: number, slotMs: number, items: ShellJobItem[], nowMs: number, live: boolean): Axis {
  let end = start;
  for (const it of items) {
    if (it.caught_up) continue;
    if (live && it.status === 'pending') end = Math.max(end, nowMs);
    const sent = toMs(it.started_at);
    if (!Number.isFinite(sent)) continue;
    end = Math.max(end, sent);
    const done = toMs(it.finished_at);
    if (Number.isFinite(done) && done - sent <= slotMs) end = Math.max(end, done);
  }
  return { start, end: Math.max(end, start + MIN_SPAN_MS) };
}

/** "GMT+7" for `timeZone` at that instant. */
export function offsetLabel(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en', { timeZone, timeZoneName: 'shortOffset' }).formatToParts(new Date(iso));
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? timeZone;
}

/** Ticks at 0/25/50/75/100% as wall-clock times in `timeZone`. */
export function axisTicks(axis: Axis, timeZone: string): Tick[] {
  const span = axis.end - axis.start;
  const second = span <= SECONDS_UP_TO_MS ? '2-digit' : undefined;
  let prevDay = '';
  return [0, 25, 50, 75, 100].map((pct) => {
    const at = new Date(axis.start + (span * pct) / 100).toISOString();
    const time = formatDateTime(at, timeZone, { year: undefined, month: undefined, day: undefined, second });
    const day = formatDateTime(at, timeZone, { year: undefined, weekday: 'short', hour: undefined, minute: undefined });
    const date = pct === 0 ? `${day} · ${offsetLabel(at, timeZone)}` : day !== prevDay ? day : undefined;
    prevDay = day;
    return { pct, time, date };
  });
}

/** "+45 s", "+12 min", "+3 h", "+2 d": how long after the axis end a device ran. */
export function lateLabel(ms: number): string {
  if (ms < 60_000) return `+${Math.max(1, Math.round(ms / 1000))} s`;
  if (ms < 3_600_000) return `+${Math.round(ms / 60_000)} min`;
  if (ms < 86_400_000) return `+${Math.round(ms / 3_600_000)} h`;
  return `+${Math.round(ms / 86_400_000)} d`;
}

/** "+12 min", "+1 h 31 min", "+2 d 2 h": how long after the axis end a late answer came. */
export function lateAnswerLabel(ms: number): string {
  const min = Math.max(1, Math.round(ms / 60_000));
  if (min < 60) return `+${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `+${h} h${min % 60 ? ` ${min % 60} min` : ''}`;
  return `+${Math.floor(h / 24)} d${h % 24 ? ` ${h % 24} h` : ''}`;
}

/** One device's bar on the axis. In a no-timeout run a device sent with no answer yet is a marker. */
export function itemBar(it: ShellJobItem, axis: Axis, nowMs: number, noTimeout = false): Bar {
  if (it.status === 'waiting') return { kind: 'waiting', sentPct: 0, endPct: 100 };
  const sent = toMs(it.started_at);
  if (!Number.isFinite(sent)) return { kind: 'none', sentPct: 0, endPct: 0 };
  if (noTimeout && it.status === 'sent') {
    const at = pctOf(sent, axis);
    return { kind: 'sent', sentPct: at, endPct: at };
  }
  const kind: BarKind = it.status === 'running' ? 'running' : 'done';
  if (it.caught_up && sent > axis.end) {
    return { kind, sentPct: 100 - PIN_PCT, endPct: 100, late: lateLabel(sent - axis.end) };
  }
  const finished = toMs(it.finished_at);
  const sentPct = pctOf(sent, axis);
  const endPct = pctOf(Number.isFinite(finished) ? finished : nowMs, axis);
  const bar: Bar = { kind, sentPct, endPct: clampPct(Math.max(endPct, sentPct + MIN_BAR_PCT)) };
  // A no-timeout answer after the axis end: the bar runs off the edge, labelled with how late it came.
  if (noTimeout && Number.isFinite(finished) && finished > axis.end) bar.lateAnswer = lateAnswerLabel(finished - axis.end);
  return bar;
}

/** By send time; devices never sent come last, by device number. */
export function compareItems(a: ShellJobItem, b: ShellJobItem): number {
  const sa = toMs(a.started_at);
  const sb = toMs(b.started_at);
  const hasA = Number.isFinite(sa);
  const hasB = Number.isFinite(sb);
  if (hasA !== hasB) return hasA ? -1 : 1;
  if (hasA && sa !== sb) return sa - sb;
  return byDeviceNumber.compare(a.device_number, b.device_number);
}

/**
 * When a run started, as the runs list says it: "2 min ago", "3 h ago",
 * "Yesterday", then the date ("Oct 10", with the year once it is not this year).
 */
export function startedAgo(iso: string, timeZone: string, nowMs: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const mins = Math.floor((nowMs - t) / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const day = (ms: number) => formatDateTime(new Date(ms).toISOString(), timeZone, { hour: undefined, minute: undefined });
  if (day(t) === day(nowMs)) return `${Math.floor(mins / 60)} h ago`;
  // ponytail: "a day before now" is yesterday except across a DST change hour; good enough for a label.
  if (day(t) === day(nowMs - 86_400_000)) return 'Yesterday';
  const year = (ms: number) => formatDateTime(new Date(ms).toISOString(), timeZone, { month: undefined, day: undefined, hour: undefined, minute: undefined });
  return formatDateTime(iso, timeZone, { year: year(t) === year(nowMs) ? undefined : 'numeric', hour: undefined, minute: undefined });
}

/** "2:01:24.512 PM" in `timeZone`, dated when it is not today: "Oct 11, 2:01:24.512 PM". */
export function formatInstant(iso: string, timeZone: string, nowMs: number, withMs = true): string {
  const day = (v: string) => formatDateTime(v, timeZone, { hour: undefined, minute: undefined });
  const today = day(iso) === day(new Date(nowMs).toISOString());
  return formatDateTime(iso, timeZone, {
    year: undefined,
    month: today ? undefined : 'short',
    day: today ? undefined : 'numeric',
    second: '2-digit',
    fractionalSecondDigits: withMs ? 3 : undefined,
  });
}
